import React, { useState, useEffect, useCallback } from 'react';
import { Shield, Plus, Edit2, Trash2, X, Check, Loader2, ChevronRight, ChevronDown, AlertTriangle } from 'lucide-react';
import orgApi from '../api/organizations';
import { useAuth } from '../auth/AuthProvider';
import { APP_PAGES, PAGE_PERMISSIONS, PERMISSION_PAGES_MAP } from '../config/permissions';

const RESOURCES = ['events', 'assets', 'sbom', 'users', 'roles', 'playbooks', 'chatops', 'reports', 'system', 'orgs', 'logs'];
const ACTIONS = ['read', 'create', 'update', 'delete'];
// Full action list per resource (only needed for resources that differ from standard CRUD)
const RESOURCE_ACTIONS = {
  events:    ['read', 'create'],
  sbom:      ['read', 'generate'],
  users:     ['read', 'create', 'update', 'delete', 'password_reset'],
  roles:     ['read', 'create', 'update', 'delete', 'assign'],
  playbooks: ['view', 'approve'],
  chatops:   ['view', 'execute'],
  system:    ['update'],
  orgs:      ['create'],
  logs:      ['read'],
};

const getRolePages = (effectivePerms) => {
  const pages = [];
  (effectivePerms || []).forEach(p => {
    const mapped = PERMISSION_PAGES_MAP[p];
    if (mapped) pages.push(...mapped);
  });
  return [...new Set(pages)];
};

const RoleManager = () => {
  const { user } = useAuth();
  const tenantId = user?.tenant_id;

  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [roleName, setRoleName] = useState('');
  const [roleDescription, setRoleDescription] = useState('');
  const [selectedPages, setSelectedPages] = useState(new Set());
  const [expandedPages, setExpandedPages] = useState(new Set());
  const [selectedSubPerms, setSelectedSubPerms] = useState(new Set());
  const [parentRoleId, setParentRoleId] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const loadData = useCallback(async () => {
    if (!tenantId) return;
    setLoading(true);
    try {
      const [rolesRes, permsRes] = await Promise.all([
        orgApi.getRoles(tenantId),
        orgApi.getPermissions(tenantId),
      ]);
      setRoles(rolesRes.roles || []);
      setPermissions(permsRes.permissions || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load roles');
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => { loadData(); }, [loadData]);

  const openCreateModal = () => {
    setEditingRole(null);
    setRoleName('');
    setRoleDescription('');
    setSelectedPages(new Set());
    setExpandedPages(new Set());
    setSelectedSubPerms(new Set());
    setParentRoleId('');
    setError(null);
    setModalOpen(true);
  };

  const openEditModal = (role) => {
    setEditingRole(role);
    setRoleName(role.name);
    setRoleDescription(role.description || '');
    const directPerms = role.direct_permissions || [];
    setSelectedSubPerms(new Set(directPerms));
    const simplePages = new Set();
    APP_PAGES.forEach(page => {
      if (page.actions.length === 0) {
        const pagePerms = PAGE_PERMISSIONS[page.path] || [];
        if (pagePerms.some(p => directPerms.includes(p))) {
          simplePages.add(page.path);
        }
      }
    });
    setSelectedPages(simplePages);
    setExpandedPages(new Set());
    setParentRoleId(role.parent_role_id || '');
    setError(null);
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!roleName.trim()) {
      setError('Role name is required');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const permsSet = new Set(selectedSubPerms);
      selectedPages.forEach(page => {
        const mapped = PAGE_PERMISSIONS[page];
        if (mapped) mapped.forEach(p => permsSet.add(p));
      });
      const payload = {
        name: roleName.trim(),
        description: roleDescription,
        permissions: Array.from(permsSet),
        parent_role_id: parentRoleId || null,
      };
      if (editingRole) {
        await orgApi.updateRole(tenantId, editingRole.id, payload);
      } else {
        await orgApi.createRole(tenantId, payload);
      }
      setModalOpen(false);
      await loadData();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save role');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (role) => {
    setConfirmDelete(role);
  };

  const confirmDeleteRole = async () => {
    if (!confirmDelete) return;
    setDeleting(confirmDelete.id);
    setError(null);
    try {
      await orgApi.deleteRole(tenantId, confirmDelete.id);
      setConfirmDelete(null);
      await loadData();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete role');
    } finally {
      setDeleting(null);
    }
  };

  const togglePage = (pagePath) => {
    setSelectedPages(prev => {
      const next = new Set(prev);
      if (next.has(pagePath)) next.delete(pagePath);
      else next.add(pagePath);
      return next;
    });
  };

  const togglePageExpand = (pagePath) => {
    setExpandedPages(prev => {
      const next = new Set(prev);
      if (next.has(pagePath)) next.delete(pagePath);
      else next.add(pagePath);
      return next;
    });
  };

  const toggleSubPerm = (permName) => {
    setSelectedSubPerms(prev => {
      const next = new Set(prev);
      if (next.has(permName)) next.delete(permName);
      else next.add(permName);
      return next;
    });
  };

  const toggleAllPagePerms = (page, checked) => {
    setSelectedSubPerms(prev => {
      const next = new Set(prev);
      page.actions.forEach(a => {
        if (checked) next.add(a.perm);
        else next.delete(a.perm);
      });
      return next;
    });
  };

  const getPagePermsState = (page) => {
    const perms = page.actions.map(a => a.perm);
    const selected = perms.filter(p => selectedSubPerms.has(p));
    if (selected.length === 0) return 'none';
    if (selected.length === perms.length) return 'all';
    return 'partial';
  };

  // OLD permission-based toggle — kept for reference
  // const togglePermission = (permName) => { ... };
  // const isInherited = (permName) => { ... };

  const getInheritanceChain = (role) => {
    const chain = [];
    let current = role;
    while (current?.parent_role_id) {
      const parent = roles.find(r => r.id === current.parent_role_id);
      if (parent) {
        chain.push(parent);
        current = parent;
      } else {
        break;
      }
    }
    return chain;
  };

  // OLD: const permissionExists = (resource, action) => {
  //   const allowed = RESOURCE_ACTIONS[resource] ?? ACTIONS;
  //   return allowed.includes(action);
  // };

  if (!tenantId) {
    return <div className="p-8 text-center text-gray-500">No active organization selected.</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Shield className="w-6 h-6 text-cyan-400" />
          <div>
            <h2 className="text-lg font-bold text-white">Role Manager</h2>
            <p className="text-xs text-gray-500">Create and manage custom roles with granular permissions</p>
          </div>
        </div>
        <button
          onClick={openCreateModal}
          className="flex items-center gap-2 px-4 py-2 bg-cyan-400 text-gray-950 rounded-lg text-sm font-bold hover:bg-cyan-300 transition-colors"
        >
          <Plus className="w-4 h-4" />
          Create Role
        </button>
      </div>

      {error && (
        <div className="px-4 py-3 bg-red-500/10 border border-red-500/30 rounded-lg text-sm text-red-400">
          {error}
          <button onClick={() => setError(null)} className="ml-2 text-red-300 hover:text-red-200">✕</button>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-6 h-6 text-gray-500 animate-spin mr-3" />
          <span className="text-sm text-gray-500">Loading roles...</span>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {roles.map(role => {
            const chain = getInheritanceChain(role);
            return (
              <div key={role.id} className={`bg-gray-900 border rounded-xl p-5 ${role.is_system ? 'border-amber-500/30' : 'border-gray-700/50'}`}>
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-0.5 rounded text-xs font-bold ${role.is_system ? 'bg-amber-500/20 text-amber-400' : 'bg-cyan-500/20 text-cyan-400'}`}>
                        {role.name}
                      </span>
                      {role.is_system && <span className="text-xs text-gray-500">System</span>}
                    </div>
                    {role.description && <p className="text-xs text-gray-500 mt-1">{role.description}</p>}
                  </div>
                </div>

                {chain.length > 0 && (
                  <div className="flex items-center gap-1 text-xs text-gray-500 mb-3">
                    <span>Inherits from:</span>
                    {chain.map((p, i) => (
                      <React.Fragment key={p.id}>
                        {i > 0 && <ChevronRight className="w-3 h-3" />}
                        <span className="text-gray-400">{p.name}</span>
                      </React.Fragment>
                    ))}
                  </div>
                )}

                <div className="flex items-center justify-between text-xs text-gray-400">
                  <span>Unlocks {getRolePages(role.effective_permissions || []).length} pages</span>
                  <span>{role.member_count || 0} members</span>
                </div>

                <div className="flex gap-2 mt-4">
                  <button
                    onClick={() => openEditModal(role)}
                    className="flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs text-blue-400 bg-blue-400/10 border border-blue-400/20 rounded-md hover:bg-blue-400/20 transition-colors"
                  >
                    <Edit2 className="w-3 h-3" /> Edit
                  </button>
                  {!role.is_system && (
                    <button
                      onClick={() => handleDelete(role)}
                      disabled={deleting === role.id}
                      className="flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs text-red-400 bg-red-400/10 border border-red-400/20 rounded-md hover:bg-red-400/20 transition-colors disabled:opacity-50"
                    >
                      {deleting === role.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {confirmDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="bg-gray-900 border border-gray-700 rounded-xl w-full max-w-md p-6">
            <div className="flex items-center gap-3 mb-4">
              <AlertTriangle className="w-6 h-6 text-red-400" />
              <h3 className="text-sm font-bold text-gray-200">Delete Role</h3>
            </div>
            <p className="text-sm text-gray-400 mb-2">
              Are you sure you want to delete <span className="text-gray-200 font-semibold">{confirmDelete.name}</span>?
            </p>
            {confirmDelete.member_count > 0 && (
              <p className="text-sm text-red-400 mb-4">
                Warning: {confirmDelete.member_count} user(s) are assigned this role. They will lose these permissions.
              </p>
            )}
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setConfirmDelete(null)}
                className="px-4 py-2 text-sm text-gray-400 hover:text-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmDeleteRole}
                disabled={deleting === confirmDelete.id}
                className="flex items-center gap-2 px-4 py-2 bg-red-500 text-white rounded-lg text-sm font-bold hover:bg-red-400 transition-colors disabled:opacity-50"
              >
                {deleting === confirmDelete.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="bg-gray-900 border border-gray-700 rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b border-gray-700">
              <h3 className="text-sm font-bold text-gray-200">
                {editingRole ? 'Edit Role' : 'Create Role'}
              </h3>
              <button onClick={() => setModalOpen(false)} className="text-gray-400 hover:text-gray-200">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-5 space-y-5">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-gray-400 mb-1.5">Role Name</label>
                  <input
                    type="text"
                    value={roleName}
                    onChange={e => setRoleName(e.target.value)}
                    disabled={editingRole?.is_system}
                    placeholder="e.g., SecurityAuditor"
                    className="w-full px-3 py-2 bg-gray-800 border border-gray-700 text-gray-100 rounded-lg text-sm focus:outline-none focus:border-cyan-400 disabled:opacity-60"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1.5">Parent Role (optional)</label>
                  <select
                    value={parentRoleId}
                    onChange={e => setParentRoleId(e.target.value)}
                    disabled={editingRole?.is_system}
                    className="w-full px-3 py-2 bg-gray-800 border border-gray-700 text-gray-100 rounded-lg text-sm focus:outline-none focus:border-cyan-400 disabled:opacity-60"
                  >
                    <option value="">None (no inheritance)</option>
                    {roles.filter(r => !editingRole || r.id !== editingRole.id).map(r => (
                      <option key={r.id} value={r.id}>{r.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-xs text-gray-400 mb-1.5">Description</label>
                <input
                  type="text"
                  value={roleDescription}
                  onChange={e => setRoleDescription(e.target.value)}
                  placeholder="Describe this role's purpose..."
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-700 text-gray-100 rounded-lg text-sm focus:outline-none focus:border-cyan-400"
                />
              </div>

              <div>
                <label className="block text-xs text-gray-400 mb-3">Page Access</label>
                <div className="space-y-1">
                  {APP_PAGES.map(page => {
                    const hasActions = page.actions.length > 0;
                    const isExpanded = expandedPages.has(page.path);
                    const isSimpleChecked = selectedPages.has(page.path);
                    const permsState = hasActions ? getPagePermsState(page) : (isSimpleChecked ? 'all' : 'none');

                    return (
                      <div key={page.path} className="bg-gray-800/30 border border-gray-700/30 rounded-lg overflow-hidden">
                        <div className="flex items-center">
                          {hasActions ? (
                            <>
                              <button
                                onClick={() => togglePageExpand(page.path)}
                                className="flex items-center justify-center w-8 h-10 text-gray-500 hover:text-gray-300 transition-colors shrink-0"
                              >
                                {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                              </button>
                              <label
                                className={`flex items-center gap-3 flex-1 h-10 pr-3 cursor-pointer transition-colors ${
                                  permsState !== 'none'
                                    ? 'text-cyan-300'
                                    : 'text-gray-400 hover:text-gray-300'
                                }`}
                              >
                                <input
                                  type="checkbox"
                                  checked={permsState === 'all'}
                                  onChange={() => toggleAllPagePerms(page, permsState !== 'all')}
                                  disabled={editingRole?.is_system}
                                  className="sr-only"
                                />
                                <div className={`w-4 h-4 rounded border flex items-center justify-center transition-colors ${
                                  permsState === 'all'
                                    ? 'bg-cyan-500 border-cyan-500'
                                    : permsState === 'partial'
                                      ? 'bg-cyan-500/40 border-cyan-500'
                                      : 'bg-gray-700 border-gray-600'
                                }`}>
                                  {permsState === 'all' && <Check className="w-3 h-3 text-white" />}
                                  {permsState === 'partial' && <div className="w-2 h-0.5 bg-white rounded" />}
                                </div>
                                <span className="text-sm font-medium">{page.label}</span>
                              </label>
                            </>
                          ) : (
                            <label
                              className={`flex items-center gap-3 flex-1 h-10 px-3 cursor-pointer transition-colors ${
                                isSimpleChecked
                                  ? 'bg-cyan-500/10 text-cyan-300'
                                  : 'text-gray-400 hover:text-gray-300 hover:bg-gray-800/50'
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={isSimpleChecked}
                                onChange={() => togglePage(page.path)}
                                disabled={editingRole?.is_system}
                                className="sr-only"
                              />
                              <div className={`w-4 h-4 rounded border flex items-center justify-center transition-colors ${
                                isSimpleChecked
                                  ? 'bg-cyan-500 border-cyan-500'
                                  : 'bg-gray-700 border-gray-600'
                              }`}>
                                {isSimpleChecked && <Check className="w-3 h-3 text-white" />}
                              </div>
                              <span className="text-sm">{page.label}</span>
                            </label>
                          )}
                        </div>

                        {hasActions && isExpanded && (
                          <div className="pb-2 pl-8 pr-3 space-y-0.5">
                            {page.actions.map(action => {
                              const isSubChecked = selectedSubPerms.has(action.perm);
                              return (
                                <label
                                  key={action.perm}
                                  className={`flex items-center gap-3 px-3 py-1.5 rounded cursor-pointer transition-colors ${
                                    isSubChecked
                                      ? 'bg-cyan-500/5 text-cyan-300'
                                      : 'text-gray-500 hover:text-gray-400 hover:bg-gray-800/30'
                                  }`}
                                >
                                  <input
                                    type="checkbox"
                                    checked={isSubChecked}
                                    onChange={() => toggleSubPerm(action.perm)}
                                    disabled={editingRole?.is_system}
                                    className="sr-only"
                                  />
                                  <div className={`w-3.5 h-3.5 rounded border flex items-center justify-center transition-colors ${
                                    isSubChecked
                                      ? 'bg-cyan-500 border-cyan-500'
                                      : 'bg-gray-700 border-gray-600'
                                  }`}>
                                    {isSubChecked && <Check className="w-2.5 h-2.5 text-white" />}
                                  </div>
                                  <span className="text-xs">{action.label}</span>
                                  <span className="text-[10px] text-gray-600 ml-auto">{action.perm}</span>
                                </label>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                {/* OLD permission matrix — kept for reference
                <div className="bg-gray-800 rounded-lg overflow-hidden">
                  <table className="w-full text-xs">
                    ...
                  </table>
                </div>
                */}
              </div>
            </div>

            <div className="flex justify-end gap-3 p-5 border-t border-gray-700">
              <button
                onClick={() => setModalOpen(false)}
                className="px-4 py-2 text-sm text-gray-400 hover:text-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex items-center gap-2 px-4 py-2 bg-cyan-400 text-gray-950 rounded-lg text-sm font-bold hover:bg-cyan-300 transition-colors disabled:opacity-50"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                {saving ? 'Saving...' : 'Save Role'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RoleManager;
